import {reportIpmAction, reportIpmVisible, type IpmMetricTags, useIpmRenderTiming} from '@github-ui/ipm-telemetry'
import {CopilotIcon} from '@primer/octicons-react'
import {Button, Link} from '@primer/react'
import {Profiler, useEffect} from 'react'

import styles from './CopilotBudgetRequestCard.module.css'

export interface CopilotBudgetRequestCardAnalytics {
  /** Pre-serialized `data-analytics-event` JSON for the "View docs" link. */
  docsEvent: string
  /** Pre-serialized `data-analytics-event` JSON for the "Review requests" CTA. */
  reviewEvent: string
}

export interface CopilotBudgetRequestCardProps {
  /** Number of pending budget requests. The card is not rendered when this is 0. */
  count: number
  /** URL of the budget-request review page. */
  reviewRequestsUrl: string
  /** Feature display name (from `MemberFeatureRequest::Feature` metadata). */
  featureName: string
  /** Feature description (from `MemberFeatureRequest::Feature` metadata). */
  featureDescription: string
  /** Docs URL for the feature. */
  docsUrl: string
  analytics: CopilotBudgetRequestCardAnalytics
}

const CARD_TELEMETRY_TAGS = {
  package: 'in-product-targeting',
  component: 'CopilotBudgetRequestCard',
  surface: 'member_requests',
  category: 'requests_from_members_page',
  nudge_id: 'copilot_premium_request_budget',
  variant: 'pending_requests',
} as const satisfies IpmMetricTags

const DOCS_TELEMETRY_TAGS = {
  ...CARD_TELEMETRY_TAGS,
  variant: 'docs_link',
} as const satisfies IpmMetricTags

const REVIEW_TELEMETRY_TAGS = {
  ...CARD_TELEMETRY_TAGS,
  variant: 'review_cta',
} as const satisfies IpmMetricTags

/**
 * Org member-requests inbox card summarizing pending "additional Copilot budget"
 * requests (IPM-474, Scene 1). Ported from the Rails ViewComponent
 * `Organizations::MemberRequests::BudgetRequestCardComponent`.
 *
 * Budget requests are a separate stream from standard feature requests:
 * `Feature::CopilotPremiumRequestBudget` is intentionally excluded from
 * `MemberFeatureRequest.total_by_feature`, so this card is rendered on its own
 * rather than through the add-on partition.
 */
export function CopilotBudgetRequestCard({
  count,
  reviewRequestsUrl,
  featureName,
  featureDescription,
  docsUrl,
  analytics,
}: CopilotBudgetRequestCardProps) {
  const renderTiming = useIpmRenderTiming(CARD_TELEMETRY_TAGS)
  const isVisible = count > 0

  useEffect(() => {
    if (isVisible) {
      reportIpmVisible(CARD_TELEMETRY_TAGS)
    }
  }, [isVisible])

  if (count <= 0) {
    return null
  }

  const card = (
    <div data-testid="copilot-budget-request-card">
      <div className="Subhead d-flex flex-wrap">
        <h2 className="Subhead-heading flex-auto" data-testid="copilot-budget-requests-heading">
          Member requests for additional Copilot budget
        </h2>
      </div>

      <div className={`Box ${styles.card}`}>
        <div className="Box-row" id="copilot_premium_request_budget">
          <div className="d-flex flex-column flex-md-row">
            <div className="d-flex flex-items-start mr-2 mr-md-3 mb-2 mb-md-0">
              <span className={`d-flex flex-row ${styles.iconCircle}`} aria-hidden="true">
                <CopilotIcon className="color-fg-muted" />
              </span>
            </div>
            <div className="d-flex flex-column col-12 col-md-8 min-width-0">
              <div className="d-flex flex-row flex-items-center flex-wrap">
                <span className="h4 mr-2">{featureName}</span>
              </div>
              <p className="color-fg-muted">
                {featureDescription}{' '}
                <Link
                  href={docsUrl}
                  className="Link--inTextBlock"
                  data-testid="copilot-budget-requests-view-docs-cta"
                  data-analytics-event={analytics.docsEvent}
                  onClick={() => reportIpmAction('click', DOCS_TELEMETRY_TAGS)}
                >
                  View {featureName} docs
                </Link>
              </p>
              <div className="d-flex flex-row">
                <Button
                  as="a"
                  href={reviewRequestsUrl}
                  variant="primary"
                  data-testid="copilot-budget-requests-review-cta"
                  data-analytics-event={analytics.reviewEvent}
                  onClick={() => reportIpmAction('cta_click', REVIEW_TELEMETRY_TAGS)}
                >
                  Review requests
                </Button>
              </div>
            </div>
            <div className="d-flex flex-md-justify-end flex-items-baseline col-md-4 mt-2 mt-md-0">
              <span className="h3 mr-2" data-testid="total-budget-requests">
                {count}
              </span>
              <span className="color-fg-muted" data-testid="budget-requests-count-label">
                {count === 1 ? 'request' : 'requests'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (!renderTiming.isEnabled) {
    return card
  }

  return (
    <Profiler id="CopilotBudgetRequestCard" onRender={renderTiming.onRender}>
      {card}
    </Profiler>
  )
}
